import React, { useEffect, useRef, useState } from "react";
import AgentsCrumb from "./AgentsCrumb";
import SignalIcon from "../signals/SignalIcon";
import { observer } from "mobx-react";
import { useHistory, useParams } from "react-router-dom";
import AgentPageSkeleton from "./AgentPageSkeleton";
import { agentsData } from "Stores/agentsData";
import { signalsData } from "Stores/signalsData";
import { userDetail } from "Stores/userDetail";
import { fetchLinkedInAccounts } from "Utils/commonAPI";
import { makeApi, URLS } from "Utils/apiURL";
import AgentSelect from "./AgentSelect";
import AgentTimezoneField from "./AgentTimezoneField";
import DayOfWeekField, { DEFAULT_DAYS, FREQUENCY_OPTIONS, daysOf, scheduleText, defaultTimezone } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";
import ConditionEditor from "./ConditionEditor";
import CasesEditor from "./CasesEditor";
import DescribedSearch from "./DescribedSearch";
import TriggerFilters from "./TriggerFilters";
import { StartKindChooser, SignalTriggerPicker, EventTriggerPicker } from "./StartsFromPicker";
import { savedOrAlwaysOn, typeOfAlwaysOnValue } from "../signals/signalMeta";
import SellerProfileLine from "../signals/SellerProfileLine";
import { eventLabel, filterConfigFor, filterSummary } from "./triggerCatalog";
import { AGENT_SEARCH_ACK, AGENT_SEARCH_UPDATE, newSearchToken, openAgentSearchChannel } from "./agentSearchChannel";
import { FALLBACK_INDUSTRIES, EMPLOYEE_RANGE_IDS } from "../DeepResearch/components/prospectFilterVocab";
import "./agentRefine.scss";
import { useAgentPageCopilot } from "./agentPageCopilot";
import { OutcomeRows } from "./SetupFlow";
import { cleanSearchName } from "./AgentCard";

// Canonical List Builder vocab for company filters, so users pick valid values (no 0-result typos).
const INDUSTRY_OPTIONS = FALLBACK_INDUSTRIES.map(x => ({ value: x, label: x }));
const EMPLOYEE_OPTIONS = EMPLOYEE_RANGE_IDS.map(x => ({ value: x, label: x }));


// Timeline tiles: the step kind's icon and tint (same set as the setup pages and the Signals drawer).
const KIND_TILE = {
  SOURCE: ["search", "violet"], EXPAND: ["user", "violet"], ENRICH: ["mail", "violet"], FILTER: ["filter", "grey"],
  WAIT: ["clock", "grey"], ACT: ["send", "amber"], APPROVE: ["check", "green"], NOTIFY: ["bell", "grey"], BRANCH: ["split", "violet"]
};
const START_ICON = { manual: "arrow", schedule: "clock", event: "bolt", signal: "sparkle" };
// Lane chip tint: the "yes" side of a split green, the "no" side red, anything else neutral.
// The step picker's groups (chips), in plain words; each covers one or more step kinds.
const PICKER_GROUPS = [
  { key: "find", label: "Find & enrich", kinds: ["SOURCE", "EXPAND", "ENRICH"] },
  { key: "filter", label: "Filter", kinds: ["FILTER"] },
  { key: "do", label: "Do", kinds: ["ACT", "APPROVE"] },
  { key: "tell", label: "Tell me", kinds: ["NOTIFY"] },
  { key: "flow", label: "Flow", kinds: ["WAIT", "BRANCH"] }
];
// One plain line under each step in the picker (the registry sends names only); unlisted types fall back to their group.
const STEP_BLURB = {
  "source.from_list": "Start from a contact list", "source.query_contacts": "Contacts that match filters you set",
  "source.from_saved_search": "People from a List Builder search", "source.from_account_list": "Start from an account list",
  "source.find_companies": "Companies that match your filters", "source.lookalike_companies": "Companies like the ones you name",
  "source.from_linkedin_search": "People from a Sales Navigator search", "source.from_linkedin_post": "People who reacted to or commented on a post",
  "source.from_linkedin_event": "People going to a LinkedIn event", "source.from_event": "The contact or account that started this run",
  "expand.people_at_company": "Decision-makers at each company", "expand.find_alternate_contact": "Someone else at the same company",
  "enrich.find_email": "A verified work email for each person", "enrich.research": "A research brief and personal email copy",
  "enrich.classify": "Sort each record with AI", "enrich.account": "Size, industry and links for the company",
  "enrich.contact": "Title, company and links for the person",
  "filter.suppress_seen": "Leave out anyone it already handled", "filter.deliverable": "Leave out do-not-contact and bad emails",
  "filter.condition": "Keep only records that match a rule", "filter.icp_match": "Keep only people who fit your ICP",
  "filter.not_at_company": "Leave out people at a company",
  "act.enroll_sequence": "Add them to one of your sequences", "act.create_task": "A task for you or a teammate",
  "act.add_to_list": "Save them to a contact list", "act.remove_from_list": "Take them off a contact list",
  "act.update_contact": "Tag them, set a stage or a field", "act.stop_sequence": "Stop or pause any sequence they are in",
  "act.opt_out": "Mark them do-not-contact", "act.export_to_crm": "Send them to your CRM",
  "act.add_note": "Leave a note on the contact", "act.add_to_account_list": "Save the company to an account list",
  "act.update_account": "Move the account to a stage",
  "notify.email": "An email to you with what it did", "notify.slack": "A message in your Slack channel",
  "notify.webhook": "Post the results to a URL", "notify.digest": "One summary email a day",
  "wait.delay": "Pause for hours or days before the next step", "wait.until": "Wait until they reply or something happens",
  "branch.if": "If yes do one thing, if no do another", "branch.switch": "A different path for each case",
  "branch.on_route": "One path if they replied, another if not"
};
const KIND_BLURB = { SOURCE: "Where the records come from", EXPAND: "Find more people", ENRICH: "Add details", FILTER: "Narrow the records",
  WAIT: "Wait before the next step", ACT: "Do something with each record", APPROVE: "Ask you first", NOTIFY: "Tell you", BRANCH: "Split into paths" };
// Friendly names for branch route labels (from branch.on_route: met / timeout).
// A split's outcome rows read as plain "If …": yes / no for conditions, the case's own name for cases.
const ROW_LABEL = { met: "If it happened", timeout: "If it didn't", true: "If yes", false: "If no", match: "If yes", no_match: "If no", else: "If none of these" };
const rowLabel = lb => ROW_LABEL[lb] || `If ${lb}`;

// "Add a step" order within each group: the most common choices first; any type not listed
// follows alphabetically (the registry itself returns types in no particular order).
const PICKER_ORDER = [
  "source.from_list", "source.query_contacts", "source.from_saved_search", "source.from_account_list",
  "source.find_companies", "source.lookalike_companies", "source.from_linkedin_search",
  "source.from_linkedin_post", "source.from_linkedin_event", "source.from_event",
  "enrich.find_email", "enrich.research", "enrich.classify",
  "filter.suppress_seen", "filter.deliverable", "filter.condition",
  "act.enroll_sequence", "act.create_task", "act.add_to_list", "act.remove_from_list",
  "act.update_contact", "act.stop_sequence", "act.opt_out", "act.export_to_crm"
];
const pickerRank = t => {
  const i = PICKER_ORDER.indexOf(t.type);
  return i < 0 ? PICKER_ORDER.length : i;
};
const byPickerOrder = (a, b) =>
  pickerRank(a) - pickerRank(b) || String(a.displayName || a.type).localeCompare(String(b.displayName || b.type));

// Switch lane labels = each case's label (in order, deduped) + the implicit "else".
const switchLabels = cases => {
  const out = [];
  (Array.isArray(cases) ? cases : []).forEach(c => {
    const l = c && c.label ? String(c.label) : "";
    if (l && !out.includes(l)) out.push(l);
  });
  out.push("else");
  return out;
};

// Shown in turn while a "Describe a change" request is worked out.
// Example asks for "Change this agent"; a click fills the box, nothing runs until Apply.
const REFINE_EXAMPLES = ["Run on weekdays at 9am", "Only VPs and above", "Add a task if they reply"];
const REFINE_STEPS = [
  "Reading your agent…",
  "Working out the change…",
  "Updating the steps…",
  "Keeping your picks in place…",
  "Checking the flow still runs…"
];

// The flow in one readable line for Copilot: "Find people -> Branch (match: Create a task; no_match: nothing)".
const describeSteps = lane => (lane || []).map(s => (s.isBranch
  ? `${s.displayName} (${(s.branchLabels || []).map(l => `${l}: ${describeSteps((s.paths || {})[l]) || "nothing"}`).join("; ")})`
  : s.displayName)).join(" -> ");

const BuildAgent = observer(() => {
  const history = useHistory();
  const { id: editId } = useParams(); // present on /agents/:id/edit → edit an existing agent
  const [editAgent, setEditAgent] = useState(null); // the loaded agent, for config merge on save
  const [notFound, setNotFound] = useState(false); // edit mode: the agent is missing or not this user's
  const [editLoaded, setEditLoaded] = useState(false); // edit mode: agent fetch finished (found or not)
  const [seqNames, setSeqNames] = useState({}); // edit mode: sequence id → name, to label saved sequence steps
  const [name, setName] = useState("");
  const [description, setDescription] = useState("Custom agent");
  const [fromAI, setFromAI] = useState(false);
  // The description typed to draft this agent. Held for this page only — see the store.
  const [askedFor, setAskedFor] = useState("");
  const [fromTemplate, setFromTemplate] = useState(false); // draft came from "Customize steps" on a template
  const [triggerKind, setTriggerKind] = useState("manual");
  const [time, setTime] = useState("08:00");
  const [frequency, setFrequency] = useState("DAILY");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [eventType, setEventType] = useState("EMAIL_REPLIED");
  const [eventConfig, setEventConfig] = useState({}); // trigger filters, e.g. { toStages: ["Working"] }
  const [signalId, setSignalId] = useState(""); // the saved signal a "when my signal fires" agent listens to
  const [suggestedSignalType, setSuggestedSignalType] = useState(""); // a described agent's signal they don't track yet
  const [sellerProfileId, setSellerProfileId] = useState(null); // a signal agent's seller profile (null: the team's default)
  const [trunk, setTrunk] = useState([]); // steps; a branch step carries .paths{label:[...]}
  const [catalog, setCatalog] = useState([]);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  // Where notifications go. Email alone is what every agent did before Slack was an option.
  const [notifyChannels, setNotifyChannels] = useState(["email"]);
  const [slackChannelId, setSlackChannelId] = useState("");
  const [requireApproval, setRequireApproval] = useState(false);
  const [dailyCap, setDailyCap] = useState(25);
  const [saving, setSaving] = useState(false);
  const [seqModal, setSeqModal] = useState(null); // {lane, i, key}
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [picker, setPicker] = useState(null); // {lane, index}
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerGroup, setPickerGroup] = useState("all");
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  // The schedule's time zone: a new agent takes the user's (profile, else browser); an edited one keeps its own.
  const [timezone, setTimezone] = useState(defaultTimezone());
  // "Describe a change": plain English in, the draft updates in place (nothing saved until Save).
  const [refineText, setRefineText] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineStep, setRefineStep] = useState(0);
  const [refineResult, setRefineResult] = useState(null); // { changes, prev } | { error }
  useEffect(() => {
    if (!refining) return undefined;
    const t = setInterval(() => setRefineStep(i => i + 1), 1600);
    return () => clearInterval(t);
  }, [refining]);

  // A drafted search refined in List Builder comes back over a channel (see DescribedSearch → "Review in
  // List Builder"): swap in the new filters, then re-describe and re-preview them.
  const trunkRef = useRef(trunk);
  trunkRef.current = trunk;
  useEffect(() => {
    const channel = openAgentSearchChannel();
    if (!channel) return undefined;
    const eachStep = (steps, fn) => steps.forEach(s => (s.isBranch ? Object.values(s.paths).forEach(p => eachStep(p, fn)) : fn(s)));
    const patch = (steps, token, changes) => steps.map(s => {
      if (s.isBranch) {
        return { ...s, paths: Object.keys(s.paths).reduce((o, k) => { o[k] = patch(s.paths[k], token, changes); return o; }, {}) };
      }
      return s.described && s.described.token === token ? { ...s, described: { ...s.described, ...changes } } : s;
    });
    channel.onmessage = async event => {
      const msg = event.data || {};
      if (msg.type !== AGENT_SEARCH_UPDATE || !msg.token || !msg.state) return;
      let step = null;
      eachStep(trunkRef.current, s => { if (s.described && s.described.token === msg.token) step = s; });
      if (!step) return; // another draft's search
      channel.postMessage({ type: AGENT_SEARCH_ACK, token: msg.token });
      setTrunk(t => patch(t, msg.token, { search: msg.state, unresolved: [], refreshing: true, edited: true }));
      const described = await agentsData.previewListBuilderSearch(msg.state, step.type === "source.find_companies");
      setTrunk(t => patch(t, msg.token, described
        ? { summary: described.summary || [], preview: described.preview || null, refreshing: false }
        : { preview: { error: "unavailable" }, refreshing: false }));
    };
    return () => channel.close();
  }, []);

  // Open the step picker fresh (advanced kinds collapsed each time).
  const openPicker = (lane, index) => { setPickerQuery(""); setPickerGroup("all"); setPicker({ lane, index }); };

  useEffect(() => {
    (async () => {
      const types = await agentsData.fetchStepTypes();
      // notify.digest is the digest toggle; find_people_icp needs the seller/ICP-approval
      // flow (built via the Daily ICP template, not the freeform builder).
      const cat = (types || []).filter(t => t.type !== "notify.digest" && t.type !== "source.find_people_icp");
      setCatalog(cat);
      // Full type map (incl. types kept out of the picker) so edit mode can preserve/label
      // steps the canvas can't add itself (e.g. the ICP source) instead of dropping them.
      const allByType = {};
      (types || []).forEach(t => { allByType[t.type] = t; });
      if (editId) {
        makeApi({ url: URLS.sequencesLite, method: "GET" }).then(res => {
          const names = {};
          (res && Array.isArray(res.data) ? res.data : []).forEach(sq => { names[sq.id] = sq.name; });
          setSeqNames(names);
        });
        const ag = await agentsData.getAgent(editId);
        if (ag) hydrateFromAgent(ag, cat, allByType);
        else setNotFound(true);
        setEditLoaded(true);
      } else {
        hydrateFromDraft(cat, allByType);
      }
    })();
    agentsData.fetchContactLists();
    agentsData.fetchSavedSearches();
    agentsData.fetchCrms(); // which CRMs are connected, for Export to CRM
    agentsData.fetchTriggerTypes(); // the events an agent can react to, and their filters
    signalsData.fetchSaved(); // the member's saved signals, for the "when my signal fires" trigger
    if (!signalsData.catalog.length) signalsData.fetchCatalog(); // signal names for the Starts summary
    agentsData.fetchSequencesLite(); // names for the "only in these sequences" trigger filter
    agentsData.fetchContactFields(); // the team's custom fields, for steps that write one
    agentsData.fetchSlackChannels(); // empty list = Slack isn't connected, so we can say so
    agentsData.fetchConditionFields(); // vocabulary for branch.if / filter.condition editors
    agentsData.fetchAccountLists(); // for the ABM source (source.from_account_list)
    agentsData.fetchTeamMembers(); // for "Get a report" on one person
    agentsData.fetchTeamGroups(); // …or on a group
    fetchLinkedInAccounts(); // for the LinkedIn-source pre-warning
    // Leaving this page ends the draft — the typed description doesn't outlive it.
    return () => agentsData.clearGeneratedPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LinkedIn source connection state (SN search needs Sales Navigator; post/event need any LinkedIn).
  const liAccounts = userDetail.activeLinkedInAccounts || [];
  const hasLinkedIn = liAccounts.length > 0;
  const hasSalesNav = liAccounts.some(a => a && a.linkedinAccountType
    && String(a.linkedinAccountType).toLowerCase() !== "classic");
  // A param whose options are integrations ({connections: "crm"}): which of them the team has connected.
  const crmConnected = value => {
    const c = (agentsData.crms || []).find(x => x.value === value);
    return c ? c.connected : true; // unknown until loaded — don't warn
  };
  const crmWarn = s => {
    const p = (s.params || []).find(x => x.connections === "crm");
    const v = p && s.values[p.key];
    if (!v || crmConnected(v)) return null;
    const o = (p.options || []).find(x => x.value === v);
    return `${o ? o.label : v} isn't connected. This step won't export anything until it is.`;
  };

  const linkedinWarn = type => {
    if (type === "source.from_linkedin_search") {
      return hasSalesNav ? null : "Needs a connected Sales Navigator account.";
    }
    if (type === "source.from_linkedin_post" || type === "source.from_linkedin_event") {
      return hasLinkedIn ? null : "Needs a connected LinkedIn account.";
    }
    return null;
  };

  const hydrateFromDraft = (cat, allByType) => {
    const draft = agentsData.consumeGeneratedDraft();
    if (!draft) return;
    setFromAI(true);
    setAskedFor(agentsData.generatedPrompt || "");
    // A composed template carries a templateKey; an AI draft doesn't. Use that to pick the copy.
    if (draft.templateKey) setFromTemplate(true);
    if (draft.name) setName(draft.name);
    if (draft.description) setDescription(draft.description);
    const t = draft.trigger || {};
    if (t.type === "schedule.recurring") {
      setTriggerKind("schedule");
      if (t.config && t.config.frequency) setFrequency(t.config.frequency);
      if (t.config && t.config.time) setTime(t.config.time);
      if (t.config) setDays(daysOf(t.config));
    } else if (t.type === "signal.saved") {
      setTriggerKind("signal");
      setSignalId((t.config && t.config.savedSignalId) || "");
      setSuggestedSignalType((t.config && t.config.signalType) || "");
    } else if (t.type && t.type.indexOf("event.") === 0) {
      setTriggerKind("event");
      setEventType(t.type.slice("event.".length));
      setEventConfig(t.config || {});
    } else {
      setTriggerKind("manual");
    }
    const byType = {};
    cat.forEach(c => { byType[c.type] = c; });
    // Drafts can branch (a reply router's positive / negative paths), so rebuild them the same way
    // a saved agent is loaded — flattening would run every path's actions on every contact.
    const nextSteps = reconstructFlow(draft.flow || [], byType, allByType || byType);
    const draftKind = t.type === "schedule.recurring" ? "schedule" : (t.type === "signal.saved" ? "signal" : (t.type && t.type.indexOf("event.") === 0 ? "event" : "manual"));
    setTrunk(applyLinkedInAsk(nextSteps, draftKind));
    const c = draft.config || {};
    // A drafted agent defaults its zone to UTC when nothing asked for one — the user's own zone is the better default.
    if (c.timezone && c.timezone !== "UTC") setTimezone(c.timezone);
    if (c.notifyMode) setNotifyMode(c.notifyMode === "IMPORTANT_ONLY" ? "ON_FAILURE" : c.notifyMode);
    if (c.notifyChannels && c.notifyChannels.length) setNotifyChannels(c.notifyChannels.slice());
    if (c.slackChannelId) setSlackChannelId(c.slackChannelId);
    if (c.approvalMode === "REQUIRED") setRequireApproval(true);
    if (c.dailyRecordCap) setDailyCap(c.dailyRecordCap);
    setSellerProfileId(c.sellerProfileId || null);
  };

  // Load an existing agent into the canvas for editing (inverse of the create flow).
  const hydrateFromAgent = (agent, cat, allByType) => {
    setEditAgent(agent);
    if (agent.name) setName(agent.name);
    if (agent.description) setDescription(agent.description);
    const t = agent.trigger || {};
    if (t.type === "schedule.recurring") {
      setTriggerKind("schedule");
      if (t.config && t.config.frequency) setFrequency(t.config.frequency);
      if (t.config && t.config.time) setTime(t.config.time);
      if (t.config) setDays(daysOf(t.config));
    } else if (t.type === "signal.saved") {
      setTriggerKind("signal");
      setSignalId((t.config && t.config.savedSignalId) || "");
    } else if (t.type && t.type.indexOf("event.") === 0) {
      setTriggerKind("event");
      setEventType(t.type.slice("event.".length));
      setEventConfig(t.config || {});
    } else {
      setTriggerKind("manual");
    }
    const byType = {};
    cat.forEach(c => { byType[c.type] = c; });
    setTrunk(reconstructFlow(agent.flow || [], byType, allByType || byType));
    const c = agent.config || {};
    setTimezone(c.timezone || "UTC"); // what it actually runs on today
    if (c.notifyMode) setNotifyMode(c.notifyMode === "IMPORTANT_ONLY" ? "ON_FAILURE" : c.notifyMode);
    if (c.notifyChannels && c.notifyChannels.length) setNotifyChannels(c.notifyChannels.slice());
    if (c.slackChannelId) setSlackChannelId(c.slackChannelId);
    if (c.approvalMode === "REQUIRED") setRequireApproval(true);
    if (c.dailyRecordCap) setDailyCap(c.dailyRecordCap);
    setSellerProfileId(c.sellerProfileId || null);
  };

  // Rebuild the editor's lane model (trunk + branch paths) from a persisted flow graph — the
  // inverse of flattenFlow(). Walks from the start node following `next`; a branch node fans its
  // labelled next-targets into paths and ends its lane (convergence isn't modelled yet). Nodes
  // whose type the canvas doesn't carry (e.g. find_people_icp, notify.digest) are skipped.
  const reconstructFlow = (flowNodes, byType, allByType) => {
    if (!Array.isArray(flowNodes) || flowNodes.length === 0) return [];
    const byId = {};
    flowNodes.forEach(n => { if (n && n.nodeId) byId[n.nodeId] = n; });
    const targets = new Set();
    flowNodes.forEach(n => (n.next || []).forEach(t => { if (t && t !== "__end") targets.add(t); }));
    const startNode = flowNodes.find(n => n.nodeId && !targets.has(n.nodeId)) || flowNodes[0];

    const walk = startId => {
      const arr = [];
      const seen = {};
      let cur = startId && startId !== "__end" ? byId[startId] : null;
      while (cur && !seen[cur.nodeId]) {
        seen[cur.nodeId] = true;
        const def = byType[cur.stepType];
        if (def && def.kind === "BRANCH") {
          // Rebuild with the persisted params so the condition/cases hydrate and (for switch)
          // the lane labels are derived from the cases.
          const b = mkStep(def, cur.params || {});
          const labels = b.branchLabels || [];
          (cur.next || []).forEach((tid, idx) => {
            const label = labels[idx];
            if (label) b.paths[label] = walk(tid);
          });
          arr.push(b);
          break; // a branch terminates its lane
        }
        if (def) {
          arr.push(mkStep(def, cur.params || {}));
        } else if (cur.stepType !== "notify.digest") {
          // Not in the picker (e.g. the ICP source) — keep it as a locked node so it's shown
          // and round-tripped intact, rather than silently dropped. (notify.digest is
          // deprecated / driven by notifyMode, so we let it fall away.)
          arr.push(mkLockedStep(cur, allByType && allByType[cur.stepType]));
        }
        // Same successor rule as the engine (RunEngine.resolveNext): the first `next` edge, or —
        // when a node has none, as in template-created flows — the following node in list order.
        // Following only `next` would stop after the first step and saving would drop the rest.
        const nextId = cur.next && cur.next.length ? cur.next[0] : null;
        if (nextId) {
          cur = nextId !== "__end" ? byId[nextId] : null;
        } else {
          const idx = flowNodes.indexOf(cur);
          cur = idx >= 0 && idx + 1 < flowNodes.length ? flowNodes[idx + 1] : null;
        }
      }
      return arr;
    };
    return walk(startNode.nodeId);
  };

  // A preserved-but-not-editable step (its type isn't offered in the picker). Rendered read-only
  // and re-emitted verbatim on save, so editing the rest of the flow never drops it.
  const mkLockedStep = (node, def) => ({
    type: node.stepType,
    kind: (def && def.kind) || "SOURCE",
    displayName: (def && def.displayName) || node.stepType,
    isBranch: false, locked: true,
    rawParams: node.params || {},
    params: [], values: {}, labels: {}
  });

  const mkStep = (def, values = {}) => {
    if (def.kind === "BRANCH") {
      const isSwitch = def.type === "branch.switch";
      const params = def.params || [];
      const vals = {};
      params.forEach(p => {
      if (values[p.key] !== undefined && values[p.key] !== null) vals[p.key] = values[p.key];
      else if (p.default !== undefined) vals[p.key] = p.default; // what the step does when left alone
    });
      const labels = isSwitch
        ? switchLabels(vals.cases)
        : (def.branchLabels && def.branchLabels.length ? def.branchLabels : ["met", "timeout"]);
      const paths = {};
      labels.forEach(l => { paths[l] = []; });
      return {
        type: def.type, kind: "BRANCH", displayName: def.displayName, isBranch: true, isSwitch,
        branchLabels: labels, params, values: vals, labels: {}, paths
      };
    }
    const params = def.params || [];
    const vals = {};
    params.forEach(p => { if (values[p.key] !== undefined && values[p.key] !== null) vals[p.key] = values[p.key]; });
    // A search source drafted from a description carries its List Builder search (saved on create).
    const described = values.search || values.searchUnresolved
      ? { search: values.search || null, summary: values.searchSummary || [], preview: values.searchPreview || null,
          unresolved: values.searchUnresolved || [], token: newSearchToken() }
      : null;
    return {
      type: def.type, kind: def.kind, displayName: def.displayName, isBranch: false,
      cost: def.cost, effect: def.effect, creditsPerRecord: def.creditsPerRecord || 0,
      inputEntity: def.inputEntity || "contact", outputEntity: def.outputEntity || def.inputEntity || "contact",
      params, values: vals, labels: {}, described
    };
  };

  // ── Lane addressing: lane === null → trunk; {bi,label} → a branch sub-path. ──
  const laneArr = (lane, src = trunk) => (lane ? src[lane.bi].paths[lane.label] : src);
  const cloneTrunk = () => trunk.map(s => s.isBranch
    ? { ...s, paths: Object.keys(s.paths).reduce((o, k) => { o[k] = [...s.paths[k]]; return o; }, {}) }
    : { ...s });

  // LinkedIn extract sources (Sales Navigator search, post, event): on a manual agent an empty URL
  // defaults to "ask each run" — one agent, a different search/post/event each run. A scheduled or
  // event agent has nobody to ask, so an ask-mode URL goes back to empty (the step shows "Needs setup").
  const applyLinkedInAsk = (steps, kind) => steps.map(s => {
    if (s.isBranch || s.locked || String(s.type || "").indexOf("source.from_linkedin") !== 0) return s;
    const url = s.values.url;
    if (kind === "manual" && (url === undefined || url === "")) {
      return { ...s, values: { ...s.values, url: "{{ask}}" }, labels: { ...s.labels, url: "Ask each run" } };
    }
    if (kind !== "manual" && url === "{{ask}}") {
      return { ...s, values: { ...s.values, url: "" }, labels: { ...s.labels, url: "" } };
    }
    return s;
  });

  const insertStep = (def, lane, index) => {
    const t = cloneTrunk();
    const arr = laneArr(lane, t);
    const step = applyLinkedInAsk([mkStep(def)], triggerKind)[0];
    if (step.isBranch) {
      arr.splice(index); // a branch is terminal on the trunk — drop anything after it
      arr.push(step);
      setTrunk(t); setPicker(null);
      return;
    }
    arr.splice(index, 0, step);
    setTrunk(t); setPicker(null);
  };
  const removeStep = (lane, i) => {
    const t = cloneTrunk();
    laneArr(lane, t).splice(i, 1);
    setTrunk(t);
  };
  const moveStep = (lane, i, dir) => {
    const arr0 = laneArr(lane);
    const j = i + dir;
    if (j < 0 || j >= arr0.length) return;
    const t = cloneTrunk();
    const arr = laneArr(lane, t);
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setTrunk(t);
  };
  const setStepValue = (lane, i, key, value, label) => {
    const t = cloneTrunk();
    const arr = laneArr(lane, t);
    let updated = {
      ...arr[i], values: { ...arr[i].values, [key]: value },
      labels: { ...arr[i].labels, [key]: label !== undefined ? label : value }
    };
    // A switch's lanes follow its cases: recompute labels and reconcile paths (keep lane contents
    // by label; new case → new empty lane; removed case → lane dropped).
    if (updated.isSwitch && key === "cases") {
      const labels = switchLabels(value);
      const newPaths = {};
      labels.forEach(l => { newPaths[l] = updated.paths[l] || []; });
      updated = { ...updated, branchLabels: labels, paths: newPaths };
    }
    arr[i] = updated;
    setTrunk(t);
  };

  // A drafted search stands in for picking a saved search.
  const usesDescribedSearch = (s, p) => p.type === "saved_search" && !s.values[p.key] && !!(s.described && s.described.search);

  // A param that only applies to some choices of another ({showWhen: {key, in: [...]}}) — e.g. the
  // team-member picker only when "Whose numbers" is one team member.
  const isShown = (s, p) => !p.showWhen || (p.showWhen.in || []).includes(s.values[p.showWhen.key]);

  const stepNeedsSetup = s => !s.isBranch && !s.locked &&
    (s.params || []).some(p => p.required && isShown(s, p) && !s.values[p.key] && !usesDescribedSearch(s, p) &&
      !(Array.isArray(s.values[p.key]) && s.values[p.key].length));

  // "Use a saved search instead": drop the drafted search so the picker shows.
  const clearDescribed = (lane, i) => {
    const t = cloneTrunk();
    const arr = laneArr(lane, t);
    arr[i] = { ...arr[i], described: null };
    setTrunk(t);
  };

  // Flatten every normal step (trunk + all sub-paths) for validation / estimates.
  const allNormal = () => {
    const out = [];
    const walk = arr => arr.forEach(s => {
      if (s.isBranch) s.branchLabels.forEach(l => walk(s.paths[l] || []));
      else out.push(s);
    });
    walk(trunk);
    return out;
  };

  // Record type flowing into trunk position idx (sources/expands hop it; branch keeps it).
  // A step that works on anything ("any") leaves the type as it was.
  const entityAt = idx => {
    let e = "contact";
    for (let k = 0; k < idx && k < trunk.length; k++) {
      const out = trunk[k].outputEntity;
      if (!trunk[k].isBranch && out && out !== "any" && !out.includes("|")) e = out;
    }
    return e;
  };

  // What a step accepts: "any" takes whatever the run carries; otherwise a "|" list of the
  // record types it understands ("contact|company"), defaulting to contacts.
  const takesEntity = (t, entity) => {
    const takes = t.inputEntity || "contact";
    return takes === "any" || takes.split("|").includes(entity);
  };

  // A team with no named custom fields has none to offer — the step then says so rather than
  // showing an empty dropdown with no explanation.
  const customFieldOptions = () => (agentsData.contactFields || [])
    .filter(f => String(f.value || "").toLowerCase().startsWith("customfield"))
    .map(f => ({ value: f.value, label: f.label }));

  const normals = allNormal();
  // "Contacts / run" caps how many people or companies a run pulls. An agent whose sources only read
  // numbers (a report, the team's credits, sequences, inboxes) has nothing to cap, so it's hidden.
  const sources = normals.filter(s => s.kind === "SOURCE");
  const capApplies = !sources.length || sources.some(s => !["report", "team", "sequence", "inbox", "competitor"].includes(s.outputEntity));
  // Approval pauses a run before it acts on records; an agent that only reads and reports (a digest) has nothing to approve.
  const actsOnRecords = normals.some(s => s.kind === "ACT");
  // The seller profile decides who the competitors are and how leads are judged, so show it wherever it's read.
  const usesSellerProfile = triggerKind === "signal" || sources.some(s => s.outputEntity === "competitor");
  const missingRequired = normals.some(stepNeedsSetup);
  const canSubmit = name.trim() && trunk.length > 0 && !missingRequired &&
    (triggerKind !== "event" || eventType) &&
    (triggerKind !== "signal" || signalId);
  const estCredits = normals.reduce(
    (sum, s) => sum + (s.cost === "CREDITS" ? (s.creditsPerRecord || 0) * Number(dailyCap || 0) : 0), 0);
  const hasOutbound = normals.some(s => s.effect === "OUTBOUND");

  const buildTrigger = () => {
    if (triggerKind === "schedule") return { type: "schedule.recurring", config: { frequency, time, ...(frequency === "WEEKLY" ? { days } : {}) } };
    if (triggerKind === "event") return { type: "event." + eventType, config: filterConfigFor(eventType, eventConfig) };
    if (triggerKind === "signal") {
      const alwaysOnType = typeOfAlwaysOnValue(signalId);
      return { type: "signal.saved", config: alwaysOnType ? { signalType: alwaysOnType } : { savedSignalId: signalId } };
    }
    return { type: "manual", config: {} };
  };
  const coerce = (p, raw) => {
    if (raw === undefined || raw === null) return undefined;
    if (p.type === "number") return Number(raw);
    if (p.type === "contact_ids") return String(raw).split(/[\s,]+/).map(x => x.trim()).filter(Boolean);
    return raw;
  };
  // withPreview: a draft for describe-a-change (and its Undo) keeps what the search shows; saving sends only the search.
  const paramsOf = (s, withPreview) => {
    if (s.locked) return s.rawParams || {}; // preserved verbatim
    const params = {};
    (s.params || []).forEach(p => {
      if (!isShown(s, p)) return; // a hidden choice doesn't apply, so don't save a stale one
      const v = coerce(p, s.values[p.key]);
      if (v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) params[p.key] = v;
      if (usesDescribedSearch(s, p)) {
        params.search = s.described.search;
        if (withPreview) Object.assign(params, { searchSummary: s.described.summary, searchPreview: s.described.preview, searchUnresolved: s.described.unresolved });
      }
    });
    return params;
  };

  // Emit the flow graph with explicit `next` wiring. Branch nodes fan out to each label's
  // first sub-path node (or "__end" if empty); every path's last node ends via "__end".
  const flattenFlow = withPreview => {
    const deep = JSON.parse(JSON.stringify(trunk));
    let c = 0;
    const assign = arr => arr.forEach(s => { s._id = `n${++c}`; if (s.isBranch) s.branchLabels.forEach(l => assign(s.paths[l] || [])); });
    assign(deep);
    const out = [];
    const emit = (arr, terminal) => arr.forEach((s, i) => {
      if (s.isBranch) {
        const nexts = s.branchLabels.map(l => { const p = s.paths[l] || []; return p.length ? p[0]._id : "__end"; });
        out.push({ nodeId: s._id, stepType: s.type, params: paramsOf(s, withPreview), errorPolicy: "skip", next: nexts });
        s.branchLabels.forEach(l => emit(s.paths[l] || [], ["__end"]));
      } else {
        const next = i < arr.length - 1 ? [arr[i + 1]._id] : terminal;
        out.push({ nodeId: s._id, stepType: s.type, params: paramsOf(s, withPreview), errorPolicy: "skip", next });
      }
    });
    emit(deep, ["__end"]);
    return out;
  };

  // The config the builder controls. In edit mode it overlays the saved config, so fields the canvas
  // doesn't manage (seller profile, send accounts, ICP approval, …) are preserved.
  const currentConfig = () => {
    const baseConfig = editAgent && editAgent.config ? editAgent.config : {};
    return {
        ...baseConfig,
        notifyMode, notifyChannels: notifyMode === "OFF" ? [] : notifyChannels,
        slackChannelId: slackChannelId || null,
        // Display only: if the channel list didn't load we can't resolve the name, so keep the
        // stored one instead of replacing a good name with null.
        slackChannelName: !slackChannelId ? null : ((agentsData.slackChannels || []).reduce(
          (found, c) => (c.id === slackChannelId ? c.name : found), null) || baseConfig.slackChannelName || null),
        approvalMode: requireApproval && actsOnRecords ? "REQUIRED" : "NONE", timezone,
        dailyRecordCap: capApplies ? Number(dailyCap) : null, // nothing to cap on a numbers-only agent
        ...(usesSellerProfile && sellerProfileId ? { sellerProfileId } : {})
    };
  };

  // Put a whole agent (a refine result, or the snapshot Undo restores) into the canvas. Unlike the
  // hydrate functions, every field is set, so a change can also turn something off.
  const applyAgent = agent => {
    const all = agentsData.stepTypesCache || catalog;
    const allByType = {};
    (all || []).forEach(t => { allByType[t.type] = t; });
    const byType = {};
    catalog.forEach(c => { byType[c.type] = c; });
    setName(agent.name || "");
    setDescription(agent.description || "Custom agent");
    const t = agent.trigger || {};
    const kind = t.type === "schedule.recurring" ? "schedule" : (t.type === "signal.saved" ? "signal" : (t.type && t.type.indexOf("event.") === 0 ? "event" : "manual"));
    setTriggerKind(kind);
    if (kind === "signal") setSignalId((t.config && t.config.savedSignalId) || "");
    if (kind === "schedule") {
      const cfg = t.config || {};
      setFrequency(cfg.frequency || "DAILY");
      setTime(cfg.time || "08:00");
      setDays(daysOf(cfg));
    } else if (kind === "event") {
      setEventType(t.type.slice("event.".length));
      setEventConfig(t.config || {});
    }
    setTrunk(applyLinkedInAsk(reconstructFlow(agent.flow || [], byType, allByType), kind));
    const c = agent.config || {};
    if (c.timezone) setTimezone(c.timezone);
    if (c.notifyMode) setNotifyMode(c.notifyMode === "IMPORTANT_ONLY" ? "ON_FAILURE" : c.notifyMode);
    setNotifyChannels(c.notifyChannels && c.notifyChannels.length ? c.notifyChannels.slice() : notifyChannels);
    setSlackChannelId(c.slackChannelId || "");
    setRequireApproval(c.approvalMode === "REQUIRED");
    if (c.dailyRecordCap) setDailyCap(c.dailyRecordCap);
    setSellerProfileId(c.sellerProfileId || null);
  };

  const draftNow = () => ({
    id: editId || undefined, name: name.trim(), description: description || "Custom agent",
    config: currentConfig(), trigger: buildTrigger(), flow: flattenFlow(true)
  });

  // From the box, or from Copilot (edit_agent_page). Returns { changed } or { error } for Copilot's note.
  const runRefine = async fromCopilot => {
    const text = (typeof fromCopilot === "string" ? fromCopilot : refineText).trim();
    if (!text || refining) return { error: refining ? "Still applying the last change." : "Describe the change." };
    const prev = draftNow();
    setRefining(true);
    setRefineStep(0);
    setRefineResult(null);
    const out = await agentsData.refineAgent(prev, text);
    setRefining(false);
    if (out.error) {
      setRefineResult({ error: out.error });
      return { error: out.error };
    }
    applyAgent(out.agent);
    setRefineText("");
    const changes = out.changes.length ? out.changes : ["Updated as described."];
    setRefineResult({ changes, prev });
    return { changed: changes };
  };

  // Copilot beside the builder edits THIS draft (same refine as the box) instead of drafting another.
  useAgentPageCopilot({
    kind: "builder",
    title: editId ? `Edit flow: ${name || "agent"}` : "Agent builder",
    describe: () => ({ summary: trunk.length ? `${name || "Untitled"}. ${triggerSummary()}. Steps: ${describeSteps(trunk)}.` : "No steps yet." }),
    apply: ({ instruction }) => runRefine(instruction || "")
  });

  const undoRefine = () => {
    if (refineResult && refineResult.prev) applyAgent(refineResult.prev);
    setRefineResult(null);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const payload = {
      name: name.trim(), description: description || "Custom agent",
      config: currentConfig(),
      trigger: buildTrigger(), flow: flattenFlow()
    };
    const agent = editId
      ? await agentsData.updateAgent(editId, payload)
      : await agentsData.createAgent(payload);
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const triggerSummary = () => {
    if (triggerKind === "schedule") return `Runs ${scheduleText(frequency, time, days, timezone)}`;
    if (triggerKind === "signal") {
      const picked = savedOrAlwaysOn(signalsData.saved, signalsData.catalog, signalId);
      if (picked) return `From your Signals · ${picked.displayName}`;
      const suggested = signalsData.catalog.find(d => d.id === suggestedSignalType);
      return suggested ? `From your Signals · ${suggested.label} (not tracked yet)` : "From your Signals";
    }
    if (triggerKind === "event") {
      const narrowed = filterSummary(eventType, eventConfig);
      return eventLabel(eventType) + (narrowed ? ` · ${narrowed}` : "");
    }
    return "When you run it";
  };
  // Short label for a condition value, shown in the node summary.
  const condSummary = v => {
    const n = v && Array.isArray(v.rules) ? v.rules.length : 0;
    if (!n) return "any record";
    return `${n} condition${n > 1 ? "s" : ""} · ${(v.join || "all") === "any" ? "ANY" : "ALL"}`;
  };
  const caseSummary = v => {
    const n = Array.isArray(v) ? v.length : 0;
    return n ? `${n} case${n > 1 ? "s" : ""} + otherwise` : "no cases yet";
  };

  // Display label for a saved param value. Pickers remember a label when you choose, but an agent
  // loaded for editing only has raw values (ids, enum codes), so look the label up.
  const valueLabel = (p, v) => {
    const byId = (list, key) => { const o = (list || []).find(x => x.id === v); return o ? o[key] : null; };
    switch (p.type) {
      case "contact_list": return byId(agentsData.contactLists, "name");
      case "saved_search": return byId(agentsData.savedSearches, "label");
      case "account_list": return byId(agentsData.accountLists, "name");
      case "team_member": return byId(agentsData.teamMembers, "name");
      case "team_group": return byId(agentsData.teamGroups, "name");
      case "sequence": return seqNames[v] || null;
      case "slack_channel": { const c = (agentsData.slackChannels || []).find(x => x.id === v); return c ? "#" + c.name : null; }
      case "select": { const o = (p.options || []).find(x => x.value === v); return o ? o.label : null; }
      case "condition": return condSummary(v);
      case "cases": return caseSummary(v);
      default: return null;
    }
  };

  const renderParam = (lane, s, i, p) => {
    if (!isShown(s, p)) return null;
    if (usesDescribedSearch(s, p)) {
      // Not a <label>: a label forwards clicks anywhere inside it to its first button.
      return (
        <div key={p.key} className="ag-field">
          <DescribedSearch described={s.described} companies={s.type === "source.find_companies"} agentName={name}
            onUseSavedSearch={() => clearDescribed(lane, i)} />
        </div>
      );
    }
    // "Ask each run" is only for manual agents (a scheduled run has no one to prompt), and
    // only for simple inputs the Run-now dialog can render (not the id pickers).
    const askable = triggerKind === "manual"
      && !["sequence", "contact_list", "saved_search", "contact_ids", "condition", "cases", "account_list",
        "team_member", "team_group", "slack_channel"].includes(p.type);
    // Only treat "{{ask}}" as ask-mode on manual agents — so switching to a schedule re-exposes
    // an editable field (a scheduled run can't prompt, so the value must be fixed).
    const isAsk = askable && s.values[p.key] === "{{ask}}";
    return (
    <label key={p.key} className="ag-field">
      <span className="ag-field__label">{p.label}{p.required && <b className="ag-req">required</b>}
        {askable && (
          <button type="button" className={`af-ask ${isAsk ? "is-on" : ""}`} role="switch" aria-checked={isAsk}
            onClick={() => setStepValue(lane, i, p.key, isAsk ? "" : "{{ask}}", isAsk ? "" : "Ask each run")}>
            <span className="af-ask__box">{isAsk && <i className="material-icons-outlined">check</i>}</span>
            Ask each run
          </button>
        )}
      </span>
      {isAsk ? (
        <div className="af-asknote">
          <i className="material-icons-outlined">repeat</i>
          <span>You'll be asked for this each time you run the agent.</span>
        </div>
      ) : p.type === "condition" ? (
        <ConditionEditor
          value={s.values[p.key]}
          fields={agentsData.conditionFields}
          onChange={v => setStepValue(lane, i, p.key, v, condSummary(v))} />
      ) : p.type === "cases" ? (
        <CasesEditor
          value={s.values[p.key]}
          fields={agentsData.conditionFields}
          onChange={v => setStepValue(lane, i, p.key, v, caseSummary(v))} />
      ) : p.type === "sequence" ? (
        <button type="button" className="ag-select__btn" onClick={() => setSeqModal({ lane, i, key: p.key })}>
          {(() => {
            const seqLabel = s.labels[p.key] || (s.values[p.key] ? valueLabel(p, s.values[p.key]) || "Selected sequence" : null);
            return <span className={seqLabel ? "" : "ag-select__ph"}>{seqLabel || "Select a sequence…"}</span>;
          })()}
          <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      ) : p.type === "contact_list" ? (
        <AgentSelect searchable placeholder="Select a contact list…" value={s.values[p.key] || ""}
          onChange={v => { const o = (agentsData.contactLists || []).find(x => x.id === v); setStepValue(lane, i, p.key, v, o ? o.name : v); }}
          options={(agentsData.contactLists || []).map(l => ({ value: l.id, label: l.count ? `${l.name} · ${l.count}` : l.name }))}
          /* Only a destination can start empty — a source or remove-from list must already exist. */
          {...(s.type === "act.add_to_list" ? {
            createLabel: "Create new list",
            onCreate: async listName => {
              const { list, error } = await agentsData.createContactList(listName);
              if (error) return error;
              setStepValue(lane, i, p.key, list.id, list.name);
              return null;
            }
          } : {})} />
      ) : p.type === "saved_search" ? (
        <>
          {s.described && !s.described.search && (s.described.unresolved || []).length > 0 && !s.values[p.key] && (
            <div className="af-dsearch__warn">
              <i className="material-icons-outlined">info</i>
              <span>Couldn’t build a search from the description. List Builder has no match for{" "}
                {s.described.unresolved.map(u => `“${u.value}”`).join(", ")}. Pick a saved search instead.</span>
            </div>
          )}
          <AgentSelect searchable placeholder="Select a saved search…" value={s.values[p.key] || ""}
            onChange={v => { const o = (agentsData.savedSearches || []).find(x => x.id === v); setStepValue(lane, i, p.key, v, o ? o.label : v); }}
            options={(agentsData.savedSearches || []).map(ss => ({ value: ss.id, label: ss.label }))} />
        </>
      ) : p.type === "account_list" ? (
        <AgentSelect searchable placeholder="Select an account list…" value={s.values[p.key] || ""}
          onChange={v => { const o = (agentsData.accountLists || []).find(x => x.id === v); setStepValue(lane, i, p.key, v, o ? o.name : v); }}
          options={(agentsData.accountLists || []).map(l => ({ value: l.id, label: l.count ? `${l.name} · ${l.count}` : l.name }))} />
      ) : p.type === "team_member" ? (
        <AgentSelect searchable placeholder="Select a team member…" value={s.values[p.key] || ""}
          onChange={v => { const o = (agentsData.teamMembers || []).find(x => x.id === v); setStepValue(lane, i, p.key, v, o ? o.name : v); }}
          options={(agentsData.teamMembers || []).map(m => ({ value: m.id, label: m.name }))} />
      ) : p.type === "team_group" ? (
        (agentsData.teamGroups || []).length === 0 ? (
          <span className="ag-hint">Your team has no groups yet, create them in Settings → Team.</span>
        ) : (
          <AgentSelect searchable placeholder="Select a group…" value={s.values[p.key] || ""}
            onChange={v => { const o = (agentsData.teamGroups || []).find(x => x.id === v); setStepValue(lane, i, p.key, v, o ? o.name : v); }}
            options={(agentsData.teamGroups || []).map(g => ({ value: g.id, label: g.count ? `${g.name} · ${g.count}` : g.name }))} />
        )
      ) : p.type === "contact_field" ? (
        // The team's own custom fields — the only contact fields an agent writes directly.
        customFieldOptions().length === 0 ? (
          <span className="ag-hint">
            Your team hasn’t named any custom fields yet. Name them in Settings and they’ll appear here.
          </span>
        ) : (
          <AgentSelect searchable placeholder="Select a custom field…" value={s.values[p.key] || ""}
            onChange={v => { const o = customFieldOptions().find(x => x.value === v); setStepValue(lane, i, p.key, v, o ? o.label : v); }}
            options={customFieldOptions()} />
        )
      ) : p.type === "slack_channel" ? (
        <AgentSelect value={s.values[p.key] || ""} searchable
          onChange={v => { const c = (agentsData.slackChannels || []).find(x => x.id === v); setStepValue(lane, i, p.key, v, c ? "#" + c.name : ""); }}
          options={[{ value: "", label: "Your team's channel" }].concat(
            (agentsData.slackChannels || []).map(c => ({
              value: c.id, label: "#" + c.name + (c.teamDefault ? " (team's channel)" : "")
            })))} />
      ) : p.type === "select" ? (
        <AgentSelect value={s.values[p.key] || ""}
          onChange={v => { const o = (p.options || []).find(x => x.value === v); setStepValue(lane, i, p.key, v, o ? o.label : v); }}
          options={(p.options || []).map(o => ({
            value: o.value,
            label: p.connections === "crm" && !crmConnected(o.value) ? `${o.label} (not connected)` : o.label
          }))} />
      ) : p.type === "industry" ? (
        <AgentSelect searchable placeholder="Select an industry…" searchPlaceholder="Search industries…"
          value={s.values[p.key] || ""} onChange={v => setStepValue(lane, i, p.key, v, v)} options={INDUSTRY_OPTIONS} />
      ) : p.type === "employee_band" ? (
        <AgentSelect placeholder="Select a size…"
          value={s.values[p.key] || ""} onChange={v => setStepValue(lane, i, p.key, v, v)} options={EMPLOYEE_OPTIONS} />
      ) : p.type === "number" ? (
        <input className="ag-input" type="number" placeholder={p.placeholder || ""} value={s.values[p.key] || ""} onChange={e => setStepValue(lane, i, p.key, e.target.value)} />
      ) : p.type === "textarea" ? (
        <textarea className="ag-input" rows={Math.min(8, Math.max(3, String(s.values[p.key] || "").split("\n").length + 1))}
          placeholder={p.placeholder || ""} value={s.values[p.key] === "{{ask}}" ? "" : (s.values[p.key] || "")}
          onChange={e => setStepValue(lane, i, p.key, e.target.value)} />
      ) : p.type === "contact_ids" ? (
        <textarea className="ag-input" rows={2} placeholder="Contact IDs, comma or newline separated" value={s.values[p.key] || ""} onChange={e => setStepValue(lane, i, p.key, e.target.value)} />
      ) : (
        <input className="ag-input" placeholder={p.placeholder || ""} value={s.values[p.key] === "{{ask}}" ? "" : (s.values[p.key] || "")} onChange={e => setStepValue(lane, i, p.key, e.target.value)} />
      )}
      {p.hint && !isAsk && <span className="ag-hint">{p.hint}</span>}
    </label>
    );
  };

  // A small "+" on the line between two steps: insert a step there.
  const Insert = ({ lane, index }) => (
    <li className="af-ins"><button type="button" className="af-ins__btn" aria-label="Add a step here" onClick={() => openPicker(lane, index)}>+</button></li>
  );
  // The end of a lane: add a step after the last one.
  const AddRow = ({ lane, index, first }) => (
    <li className={lane ? "af-addrow af-addrow--lane" : "ag-sf__item af-addrow"}>
      {!lane && <span className="ag-sf__dot af-addrow__dot" aria-hidden="true">+</span>}
      <button type="button" className="af-addrow__btn" onClick={() => openPicker(lane, index)}>
        <SignalIcon id="plus" size={15} />{first ? "Add your first step" : "Add a step"}
      </button>
    </li>
  );

  const tile = kind => {
    const [icon, tone] = KIND_TILE[kind] || ["bolt", "grey"];
    return <span className={`ag-sf__tile is-${tone}`}><SignalIcon id={icon} size={18} /></span>;
  };
  const StepControls = ({ lane, i, arrLen }) => (
    <span className="af-ctrl">
      <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => moveStep(lane, i, -1)}><i className="material-icons-outlined">arrow_upward</i></button>
      <button type="button" aria-label="Move down" disabled={i === arrLen - 1} onClick={() => moveStep(lane, i, 1)}><i className="material-icons-outlined">arrow_downward</i></button>
      <button type="button" aria-label="Remove step" onClick={() => removeStep(lane, i)}><i className="material-icons-outlined">close</i></button>
    </span>
  );
  const Warn = ({ text, to, link }) => (
    <div className="af-liwarn">
      <i className="material-icons-outlined">info</i>
      <span>{text}{" "}
        <a className="af-liwarn__link" href={to} onClick={e => { e.preventDefault(); history.push(to); }}>{link}</a>
      </span>
    </div>
  );

  // The ICP source shows which ICP it uses (the step stays locked in the flow; the ICP is set on the agent page).
  const icpId = editAgent && editAgent.config && editAgent.config.icpSavedSearchId;
  const icpSearch = icpId && (agentsData.savedSearches || []).find(x => x.id === icpId);
  const icpLine = (
    <span className="af-icp">
      {icpId
        ? <span>Uses your ICP: <b>{icpSearch ? cleanSearchName(icpSearch.label) : "your saved search"}</b></span>
        : <span className="af-icp__none">No ICP yet</span>}
      {editId
        ? <button type="button" className="ag-link" onClick={() => history.push(`/agents/${editId}?setup=1`)}>Change ICP</button>
        : <span className="af-icp__later">Set after you create it</span>}
    </span>
  );

  // One step: marker on the line, then a card with its tile, plain name, tags and every setting.
  const renderStepNode = (lane, s, i, arrLen) => {
    const needs = stepNeedsSetup(s);
    const isIcpStep = s.type === "source.find_people_icp";
    return (
      <li className={lane ? "ag-sf__lanestep" : "ag-sf__item"}>
        <span className={`ag-sf__dot${lane ? " ag-sf__dot--lane" : ""}`} aria-hidden="true" />
        <div className={`ag-sf__card ag-sf__card--step${needs ? " af-needs" : ""}${s.locked ? " af-locked" : ""}`}>
          {tile(s.kind)}
          <div className="ag-sf__main">
            <div className="ag-sf__name-row">
              <span className="ag-sf__title">{s.displayName}</span>
              {s.cost === "CREDITS" && <span className="ag-sf__tag">Uses credits</span>}
              {s.effect === "OUTBOUND" && <span className="ag-sf__tag is-ask">Sends</span>}
              {s.locked ? (isIcpStep ? null : <i className="material-icons-outlined af-lock" title="Configured on the agent page">lock</i>) : <StepControls lane={lane} i={i} arrLen={arrLen} />}
            </div>
            {isIcpStep ? icpLine : s.locked && <span className="ag-sf__hint">Set up on the agent page, can’t edit here.</span>}
            {!s.locked && linkedinWarn(s.type) && <Warn text={linkedinWarn(s.type)} to="/settings/account/linkedin" link="Connect an account" />}
            {!s.locked && crmWarn(s) && <Warn text={crmWarn(s)} to="/settings/crm" link="Connect it" />}
            {!s.locked && (s.params || []).map(p => renderParam(lane, s, i, p))}
          </div>
        </div>
      </li>
    );
  };

  // A split: the card asks the question (condition or cases), then one row per route inside it, each with its own
  // steps and "Add a step"; an empty route reads "Nothing extra".
  const renderBranch = (s, i) => (
    <li className="ag-sf__item af-branch">
      <span className="ag-sf__dot" aria-hidden="true" />
      <div className="ag-sf__card ag-sf__card--step af-branch__head">
        {tile("BRANCH")}
        <div className="ag-sf__main">
          <div className="ag-sf__name-row">
            <span className="ag-sf__title">{s.displayName}</span>
            <span className="af-ctrl">
              <button type="button" aria-label="Remove split" onClick={() => removeStep(null, i)}><i className="material-icons-outlined">close</i></button>
            </span>
          </div>
          {(s.params || []).map(p => renderParam(null, s, i, p))}
          <OutcomeRows rows={s.branchLabels.map(lb => {
            const lane = { bi: i, label: lb };
            const sub = s.paths[lb] || [];
            return {
              label: rowLabel(lb),
              body: (
                <>
                  {!sub.length && <span className="af-row__none">Nothing extra</span>}
                  <ol className="ag-sf__lanetl">
                    {sub.map((ss, j) => (
                      <React.Fragment key={j}>
                        {j > 0 && <Insert lane={lane} index={j} />}
                        {renderStepNode(lane, ss, j, sub.length)}
                      </React.Fragment>
                    ))}
                    <AddRow lane={lane} index={sub.length} />
                  </ol>
                </>
              )
            };
          })} />
        </div>
      </div>
    </li>
  );

  // ── Contextual palette ──
  const trunkEndsInBranch = trunk.length > 0 && trunk[trunk.length - 1].isBranch;
  const canBranchHere = picker && picker.lane === null && picker.index === trunk.length && !trunkEndsInBranch;
  const pickerEntity = picker && picker.lane === null ? entityAt(picker.index) : "contact";
  // "The start" = no records have been sourced before this point yet. You can only source there —
  // you can't enrich/filter/act on records that don't exist.
  const hasRecordsBefore = idx => trunk.slice(0, idx).some(s => s.kind === "SOURCE" || s.kind === "EXPAND");
  const atStart = picker && picker.lane === null && !hasRecordsBefore(picker.index);
  const addable = t => {
    if (picker && picker.lane) {
      // Sub-path (after a branch): downstream contact steps only — no source/expand/branch.
      return t.kind !== "SOURCE" && t.kind !== "EXPAND" && t.kind !== "BRANCH" && takesEntity(t, "contact");
    }
    if (t.type === "source.from_event") return atStart && (triggerKind === "event" || triggerKind === "signal");
    if (atStart) return t.kind === "SOURCE";           // start: only sources make sense
    if (t.kind === "SOURCE") return false;             // already have records — hide sources
    if (t.kind === "BRANCH") return canBranchHere;
    return takesEntity(t, pickerEntity);
  };
  // Every addable step, grouped; the search and the group chips narrow it (nothing hides behind "More options").
  const blurbOf = t => STEP_BLURB[t.type] || KIND_BLURB[t.kind] || "";
  const q = pickerQuery.trim().toLowerCase();
  const allGroups = PICKER_GROUPS
    .map(g => ({ ...g, items: catalog.filter(t => g.kinds.includes(t.kind) && addable(t)).sort(byPickerOrder) }))
    .filter(g => g.items.length);
  const shownGroups = allGroups
    .filter(g => pickerGroup === "all" || g.key === pickerGroup)
    .map(g => ({ ...g, items: q ? g.items.filter(t => `${t.displayName} ${blurbOf(t)}`.toLowerCase().includes(q)) : g.items }))
    .filter(g => g.items.length);
  // Same words as the step cards: the registry's per-record figure isn't reliable for every step (research costs more).
  const costTag = t => (t.cost === "CREDITS" ? "Uses credits" : null);

  const renderGroup = g => (
    <div key={g.key} className="af-picker__group">
      <div className="af-picker__kind">{g.label}</div>
      {g.items.map(t => (
        <button key={t.type} className="af-picker__item" onClick={() => insertStep(t, picker.lane, picker.index)}>
          {tile(t.kind)}
          <span className="af-picker__txt"><b>{t.displayName}</b>{blurbOf(t) && <span>{blurbOf(t)}</span>}</span>
          <span className="af-picker__meta">
            {costTag(t) && <span className="af-tag">{costTag(t)}</span>}
            {t.effect === "OUTBOUND" && <span className="af-tag af-tag--send">Sends</span>}
          </span>
        </button>
      ))}
    </div>
  );

  // Edit mode: hold the page loader until the agent is fetched, so neither an empty builder
  // nor the not-found card flashes in first.
  if (editId && !editLoaded) {
    return <AgentPageSkeleton />;
  }
  if (notFound) {
    return (
      <div className="ag-page">
        <div className="ag-empty">
          <div className="ag-empty__title">Agent not found</div>
          <div className="ag-empty__actions">
            <button className="ag-btn ag-btn--primary" onClick={() => history.push("/agents")}><i className="material-icons-outlined">arrow_back</i>Back to Agents</button>
          </div>
        </div>
      </div>
    );
  }

  const why = saving ? "" : !name.trim() ? "Name your agent" : trunk.length === 0 ? "Add at least one step"
    : missingRequired ? "Fill in the highlighted steps" : "";

  return (
    <div className="ag-page ag-sf af-page">
      <div className="ag-page__head">
        <div>
          <AgentsCrumb items={editId
            ? [{ label: name || "Agent", to: `/agents/${editId}` }, { label: "Edit flow" }]
            : [{ label: "Hire an agent", to: "/agents/new" }, { label: "Build" }]} />
          {editId ? (
            // Editing: the title is the agent's name, renamed in place (same as the agent page).
            renaming ? (
              <input className="ag-input ag-dhead__input af-title__input" autoFocus value={nameDraft} maxLength={80} aria-label="Agent name"
                onChange={e => setNameDraft(e.target.value)}
                onBlur={() => { if (nameDraft.trim()) setName(nameDraft.trim()); setRenaming(false); }}
                onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setNameDraft(name); setRenaming(false); } }} />
            ) : (
              <h1 className="ag-page__title">
                <button type="button" className="ag-dhead__name af-title" title="Rename" onClick={() => { setNameDraft(name); setRenaming(true); }}>
                  {name || "Agent"}<i className="material-icons-outlined">edit</i>
                </button>
              </h1>
            )
          ) : (
            <>
              <h1 className="ag-page__title">{fromTemplate ? "Customize your agent" : fromAI ? "Review your agent" : "Build an agent"}</h1>
              <p className="ag-sf__sub">
                {fromTemplate ? "Started from a template. Change or add steps, then create."
                  : fromAI ? "Drafted from your description. Check the steps, then create."
                    : "Pick how it starts, then add steps."}
              </p>
            </>
          )}
        </div>
      </div>

      <div className="af-layout">
      <div className="ag-sf__col">
        {/* What they typed to get this draft, so the flow can be checked against the ask. It lives
            only until the agent is created, from then on the agent's own summary describes it. */}
        {fromAI && !editId && !!askedFor && (
          <div className="af-asked">
            <span className="af-asked__label">You asked for</span>
            <p className="af-asked__text">{askedFor}</p>
          </div>
        )}

        {!editId && (
          <label className="ag-sf__name">
            <span>Agent name</span>
            <input className="ag-input" placeholder="Name your agent" value={name} onChange={e => setName(e.target.value)} />
          </label>
        )}
        {usesSellerProfile && <div className="ag-sf__opts"><SellerProfileLine className="sg-sp--gap" value={sellerProfileId} onChange={setSellerProfileId} /></div>}

        <ol className="ag-sf__tl">
          <li className="ag-sf__item ag-sf__item--when">
            <span className="ag-sf__dot ag-sf__dot--when"><SignalIcon id={START_ICON[triggerKind] || "arrow"} size={16} /></span>
            <div className="ag-sf__card">
              <div className="ag-sf__name-row af-when__row">
                <div className="af-when__text">
                  <span className="ag-sf__eyebrow">When</span>
                  <span className="ag-sf__title">{triggerSummary()}</span>
                </div>
                <button type="button" className="ag-link af-when__toggle" onClick={() => setTriggerOpen(o => !o)}>{triggerOpen ? "Done" : "Change"}</button>
              </div>
              {triggerOpen && (
                <>
                  <StartKindChooser value={triggerKind} onChange={k => { setTriggerKind(k); setTrunk(tr => applyLinkedInAsk(tr, k)); }} />
                  {triggerKind === "schedule" && (
                    <>
                      <div className="ag-sf__row">
                        <div className="ag-sf__field"><span className="ag-sf__label">Runs</span>
                          <AgentSelect value={frequency} onChange={setFrequency} options={FREQUENCY_OPTIONS} />
                        </div>
                        <div className="ag-sf__field"><span className="ag-sf__label">Time</span>
                          <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
                        </div>
                      </div>
                      {frequency === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
                      {/* Its own line: zone names like "(GMT-5:00) Eastern Standard Time - New York" need the width. */}
                      <div className="ag-sf__field"><span className="ag-sf__label">Time zone</span>
                        <AgentTimezoneField value={timezone} onChange={setTimezone} />
                      </div>
                    </>
                  )}
                  {triggerKind === "signal" && (
                    <div className="ag-sf__field"><span className="ag-sf__label">Which signal?</span>
                      <SignalTriggerPicker value={signalId} onChange={setSignalId} suggestedType={suggestedSignalType} onPickType={setSuggestedSignalType} />
                    </div>
                  )}
                  {triggerKind === "event" && (
                    <>
                      <div className="ag-sf__field"><span className="ag-sf__label">What happens in Salesgear?</span>
                        <EventTriggerPicker value={eventType} onChange={v => { setEventType(v); setEventConfig({}); }} />
                      </div>
                      <TriggerFilters eventType={eventType} config={eventConfig} onChange={setEventConfig} />
                    </>
                  )}
                </>
              )}
            </div>
          </li>

          {trunk.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Insert lane={null} index={i} />}
              {s.isBranch ? renderBranch(s, i) : renderStepNode(null, s, i, trunk.length)}
            </React.Fragment>
          ))}
          {!trunkEndsInBranch && <AddRow lane={null} index={trunk.length} first={trunk.length === 0} />}
        </ol>

        <div className="ag-sf__card af-settings">
          <span className="ag-sf__title">Settings</span>
          <div className="ag-sf__row">
            {capApplies && (
              <div className="ag-sf__field"><span className="ag-sf__label">Contacts per run</span>
                <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
              </div>
            )}
            <div className="ag-sf__field"><span className="ag-sf__label">Notify me</span>
              <AgentSelect value={notifyMode} onChange={setNotifyMode} options={[
                { value: "DAILY_DIGEST", label: "Daily digest" },
                { value: "EVERY_RUN", label: "After every run" },
                { value: "ON_FAILURE", label: "Only when a run fails" },
                { value: "OFF", label: "Off" }
              ]} />
            </div>
          </div>
          {notifyMode !== "OFF" && (
            <div className="ag-sf__row">
              <div className="ag-sf__field"><span className="ag-sf__label">Send it to</span>
                <div className="ag-chanpick">
                  {["email", "slack"].map(ch => {
                    const on = notifyChannels.indexOf(ch) !== -1;
                    const noSlack = ch === "slack" && agentsData.slackChannelsLoaded
                      && !(agentsData.slackChannels || []).length;
                    return (
                      <label key={ch} className={"ag-chanpick__opt" + (noSlack ? " ag-chanpick__opt--off" : "")}>
                        <input type="checkbox" checked={on} disabled={noSlack}
                          onChange={e => {
                            const next = notifyChannels.filter(c => c !== ch);
                            if (e.target.checked) next.push(ch);
                            // Never leave it with nowhere to go: that's what "Off" is for.
                            setNotifyChannels(next.length ? next : [ch === "email" ? "slack" : "email"]);
                          }} />
                        <span>{ch === "email" ? "Email" : "Slack"}</span>
                      </label>
                    );
                  })}
                </div>
                {agentsData.slackChannelsLoaded && !(agentsData.slackChannels || []).length && (
                  <span className="ag-sf__hint">Connect Slack in Settings to send these there too.</span>
                )}
              </div>
              {notifyChannels.indexOf("slack") !== -1 && !!(agentsData.slackChannels || []).length && (
                <div className="ag-sf__field"><span className="ag-sf__label">Slack channel</span>
                  <AgentSelect value={slackChannelId} searchable onChange={setSlackChannelId}
                    options={[{ value: "", label: "Your team's channel" }].concat(
                      (agentsData.slackChannels || []).map(c => ({
                        value: c.id, label: "#" + c.name + (c.teamDefault ? " (team's channel)" : "")
                      })))} />
                </div>
              )}
            </div>
          )}
          {actsOnRecords && (
            <label className="ag-sf__check">
              <input type="checkbox" checked={requireApproval} onChange={e => setRequireApproval(e.target.checked)} />
              <span>Ask me before every run acts</span>
            </label>
          )}
          {hasOutbound && !requireApproval && (
            <span className="ag-sf__hint">The first real run asks you before it sends. After one approved run, it runs on its own.</span>
          )}
        </div>
      </div>
      {/* "Describe a change": a card beside the flow on wide screens, above it when there's no room (CSS). */}
      {trunk.length > 0 && (
        <aside className="af-side">
          <div className="af-refine af-refine--card">
            <h2 className="af-refine__head"><SignalIcon id="sparkle" size={16} />Change this agent</h2>
            <textarea
              className="af-refine__area"
              rows={3}
              placeholder="Describe a change, e.g. run at 9am on weekdays"
              value={refineText}
              disabled={refining}
              onChange={e => setRefineText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); runRefine(); } }}
            />
            <div className="af-refine__chips">
              {REFINE_EXAMPLES.map(x => (
                <button key={x} type="button" className="af-refine__chip" disabled={refining} onClick={() => setRefineText(x)}>{x}</button>
              ))}
            </div>
            <button className="ag-btn ag-btn--primary af-refine__apply" disabled={refining || !refineText.trim()} onClick={() => runRefine()}>
              <i className="material-icons-outlined">arrow_upward</i>Apply
            </button>
            {refining && (
              <div className="af-refine__working" aria-live="polite">
                <span className="af-refine__dot" />
                {REFINE_STEPS[refineStep % REFINE_STEPS.length]}
              </div>
            )}
            {!refining && refineResult && (
              <div className={`af-refine__result${refineResult.error ? " af-refine__result--error" : ""}`}>
                <i className="material-icons-outlined af-refine__icon">{refineResult.error ? "info" : "check_circle"}</i>
                <div className="af-refine__body">
                  {refineResult.error ? (
                    <>
                      <div className="af-refine__title">Couldn't make that change</div>
                      <p className="af-refine__msg">{refineResult.error}</p>
                    </>
                  ) : (
                    <>
                      <div className="af-refine__title">What changed. Review it, then save when you're happy.</div>
                      <ul className="af-refine__list">{refineResult.changes.map((c, i) => <li key={i}>{c}</li>)}</ul>
                    </>
                  )}
                </div>
                <div className="af-refine__actions">
                  {refineResult.prev && <button className="af-refine__link" onClick={undoRefine}>Undo</button>}
                  <button className="af-refine__link" onClick={() => setRefineResult(null)}>Dismiss</button>
                </div>
              </div>
            )}
          </div>
        </aside>
      )}
      </div>

      <div className="ag-sf__bar">
        <div className="ag-sf__barin">
          <div className="ag-sf__cost">
            {estCredits > 0 && <span>Up to about {estCredits} credits a run</span>}
            {editId && hasOutbound && <span className="ag-sf__note">Saving asks for your approval again before its next run sends anything.</span>}
            {!canSubmit && why && <span className="ag-sf__why">{why}</span>}
          </div>
          <button className="ag-btn" onClick={() => history.push("/agents")}>Cancel</button>
          <button className="ag-btn ag-btn--primary" disabled={!canSubmit || saving} onClick={submit}>
            {saving ? (editId ? "Saving…" : "Creating…") : (editId ? "Save changes" : "Create agent")}
          </button>
        </div>
      </div>

      {/* Step picker — contextual to the lane + record type at this point */}
      {picker && (
        <div className="af-picker__overlay" onMouseDown={() => setPicker(null)}>
          <div className="af-picker" onMouseDown={e => e.stopPropagation()}>
            <div className="af-picker__head">
              <h3>Add a step</h3>
              <button className="ag-seqm__close" onClick={() => setPicker(null)}>×</button>
            </div>
            {(picker.lane || atStart) && (
              <div className="af-picker__ctx">
                {picker.lane ? <>Adding to <b>{rowLabel(picker.lane.label)}</b></> : "Start with where the contacts or companies come from."}
              </div>
            )}
            <div className="af-picker__search">
              <SignalIcon id="search" size={15} />
              <input autoFocus aria-label="Search steps" placeholder="Search steps" value={pickerQuery}
                onChange={e => setPickerQuery(e.target.value)}
                onKeyDown={e => { if (e.key === "Escape") setPicker(null); }} />
            </div>
            {allGroups.length > 1 && (
              <div className="af-picker__chips" role="tablist">
                {[{ key: "all", label: "All" }, ...allGroups].map(g => (
                  <button key={g.key} type="button" role="tab" aria-selected={pickerGroup === g.key}
                    className={pickerGroup === g.key ? "on" : ""} onClick={() => setPickerGroup(g.key)}>{g.label}</button>
                ))}
              </div>
            )}
            <div className="af-picker__body">
              {allGroups.length === 0 && <div className="af-picker__empty">No steps available here.</div>}
              {allGroups.length > 0 && shownGroups.length === 0 && <div className="af-picker__empty">No steps match "{pickerQuery}".</div>}
              {shownGroups.map(renderGroup)}
            </div>
          </div>
        </div>
      )}

      <SequencePickerModal
        open={!!seqModal}
        currentId={seqModal ? laneArr(seqModal.lane)[seqModal.i].values[seqModal.key] : ""}
        onSelect={s => { if (seqModal) setStepValue(seqModal.lane, seqModal.i, seqModal.key, s.id, s.name); }}
        onClose={() => setSeqModal(null)}
      />
    </div>
  );
});

export default BuildAgent;
